/**
 * Internal HTTP helper for the user-management module.
 *
 * Reuses the same Firebase ID token + envelope unwrapping conventions as
 * `src/lib/api.ts` so the backend's standard response shape (`{ data, ... }`)
 * is transparent to callers. Centralising the request logic keeps the
 * service files free of fetch/headers boilerplate.
 */

import { auth } from '@/src/infrastructure/config/firebase';
import { signOut } from 'firebase/auth';
import { API_BASE } from '@/src/infrastructure/lib/api';

export { API_BASE };

export async function request<T>(
  url: string,
  options: RequestInit = {},
  retried = false,
): Promise<T> {
  const token = await auth.currentUser?.getIdToken(retried);
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> | undefined),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const res = await fetch(url, { ...options, headers });

  if (res.status === 401 && !retried && auth.currentUser) {
    return request<T>(url, options, true);
  }
  if (res.status === 401 || res.status === 403) {
    await signOut(auth).catch(() => undefined);
    if (typeof window !== 'undefined') window.location.href = '/login';
    throw new Error(res.status === 403 ? 'Admin access required' : 'Session expired');
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const message = Array.isArray(body?.message)
      ? body.message.join(', ')
      : body?.message ?? `HTTP ${res.status}`;
    throw new Error(message);
  }
  if (res.status === 204) return undefined as T;
  const body = await res.json();
  return (body?.data !== undefined ? body.data : body) as T;
}

export function buildQuery(params: Record<string, unknown>): string {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    qs.set(k, String(v));
  }
  const s = qs.toString();
  return s ? `?${s}` : '';
}
